import React, { useState, useEffect } from 'react';
import {
    Accordion, AccordionSummary, AccordionDetails, Typography, Chip, CircularProgress,
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, IconButton, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Attivita from './Attivita';

const Progetti = ({ idScheda, idCliente }) => {
    const [progetti, setProgetti] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [newProgetti, setNewProgetti] = useState({
        siglaprogetto: '',
        descrizioneprogetto: '',
        annorif: new Date().getFullYear()
    });

    const fetchProgetti = () => {
        const token = localStorage.getItem('token');
        setLoading(true);
        fetch(`/papi2/projects/getProgetti/${idScheda}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        localStorage.removeItem('token');
                        window.location.href = '/login';
                    }
                    throw new Error('Errore nel caricamento dei progetti');
                }
                return response.json();
            })
            .then((data) => {
                if (Array.isArray(data)) {
                    setProgetti(data);
                } else {
                    console.error('API non ha restituito un array di progetti:', data);
                    setProgetti([]);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('Errore nel recupero dei progetti:', error);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchProgetti();
    }, [idScheda]);

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewProgetti(prev => ({ ...prev, [name]: value }));
    };

    const handleDelete = async (idProgetto) => {
        if (!window.confirm("Sei sicuro di voler eliminare questo progetto?")) return;

        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`/papi2/projects/deleteProgetto/${idProgetto}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                fetchProgetti();
            } else {
                const msg = await response.text();
                alert(`Errore: ${msg}`);
            }
        } catch (error) {
            console.error("Errore eliminazione:", error);
        }
    };

    const handleSubmit = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch('/papi2/projects/addProgetto', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...newProgetti,
                    idscheda_fk: idScheda,
                    idcliente_fk: idCliente
                })
            });

            if (response.ok) {
                handleCloseModal();
                fetchProgetti(); // Ricarica la lista
                setNewProgetti({
                    siglaprogetto: '',
                    descrizioneprogetto: '',
                    annorif: new Date().getFullYear()
                });
            } else {
                console.error("Errore durante l'aggiunta del progetto");
            }
        } catch (error) {
            console.error("Errore di rete:", error);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>;
    }

    return (
        <Box sx={{ width: '100%', bgcolor: 'transparent' }}>
            <Box display="flex" justifyContent="space-between" mb={1} px={1}>
                <Typography variant="body2" color="text.secondary">
                    Progetti:
                </Typography>
                <Tooltip title="Aggiungi Progetto">
                    <IconButton onClick={handleOpenModal} color="primary" size="small" sx={{ bgcolor: 'rgba(25, 118, 210, 0.1)', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.2)' } }}>
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {progetti.length > 0 ? (
                progetti.map((progetto, index) => (
                    <Accordion
                        key={index}
                        disableGutters
                        square
                        sx={{
                            background: 'transparent',
                            boxShadow: 'none',
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            '&:before': { display: 'none' },
                            '&:last-child': { borderBottom: 'none' }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                flexDirection: 'row-reverse',
                                '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                    transform: 'rotate(90deg)',
                                },
                                '& .MuiAccordionSummary-content': {
                                    marginLeft: 2,
                                }
                            }}
                        >
                            <Box display="flex" alignItems="center" width="100%">
                                <Chip
                                    label={progetto.siglaprogetto}
                                    size="small"
                                    sx={{
                                        borderRadius: 1,
                                        mr: 2,
                                        fontWeight: 700,
                                        bgcolor: 'primary.main',
                                        color: 'white'
                                    }}
                                />
                                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                    {progetto.descrizioneprogetto}
                                </Typography>
                            </Box>
                            <Box ml={2}>
                                {(!progetto.num_attivita || progetto.num_attivita === 0) && (
                                    <Tooltip title="Elimina Progetto">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(progetto.idprogetto);
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <Attivita idProgetto={progetto.idprogetto} />
                        </AccordionDetails>
                    </Accordion>
                ))
            ) : (
                <Box p={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        Nessun progetto associato.
                    </Typography>
                </Box>
            )}
            <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
                <DialogTitle>Aggiungi Nuovo Progetto</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Sigla Progetto"
                                    name="siglaprogetto"
                                    value={newProgetti.siglaprogetto}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descrizione Progetto"
                                    name="descrizioneprogetto"
                                    value={newProgetti.descrizioneprogetto}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Anno Rif"
                                    name="annorif"
                                    type="number"
                                    value={newProgetti.annorif}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="inherit">Annulla</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">Salva</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Progetti;
